import { createClient } from '@supabase/supabase-js'
import type { Database } from '@/shared/types/database.types'

// Retrieve credentials from Environment Variables
// NOTE: On Vercel, usage of 'import.meta.env' requires the variables to be
// properly set in the Project Settings > Environment Variables for valid replacement at build time.
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

// Build-time development flag. This ONLY toggles mock/dev UX in the UI layer.
// It NEVER bypasses RLS. The Supabase service-role key must live exclusively in
// Edge Function environment (Deno.env) and must NEVER be referenced from client
// code: any `VITE_` variable that is referenced gets inlined into the public
// browser bundle at build time, even when the runtime branch is not taken.
export const isDevMode = import.meta.env.DEV

// Log error if in production but missing credentials (helps debug Vercel issues)
if (import.meta.env.PROD && (!supabaseUrl || !supabaseAnonKey)) {
  console.error('❌ CRITICAL: Supabase environment variables are missing in production build!')
  console.error('Please check Vercel Project Settings > Environment Variables.')
}

export const supabase = createClient<Database>(
  supabaseUrl || 'https://placeholder.supabase.co',
  supabaseAnonKey || 'placeholder-key',
  {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
      // Flow type determines session duration behavior
      // 'implicit' = shorter lived sessions (better for mobile)
      // 'pkce' = longer lived sessions (better for desktop/web)
      flowType: 'pkce',
    },
  }
)

// Helper to check if Supabase is configured with real values (not placeholders)
export const isSupabaseConfigured = () => {
  const isRealUrl = supabaseUrl &&
    !supabaseUrl.includes('placeholder') &&
    (supabaseUrl.includes('.supabase.co') || supabaseUrl.includes('localhost') || supabaseUrl.includes('127.0.0.1'))
  const isRealKey = supabaseAnonKey &&
    (supabaseAnonKey) !== 'placeholder-key' &&
    supabaseAnonKey.length > 20
  return Boolean(isRealUrl && isRealKey)
}
